import { Dispatch, SetStateAction, useState } from "react"

import { Datasets } from "../../../../constants/enums"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { IAppError } from "../../../../errors/errorTypes"
import { snackbar } from "../../../../utils/general/generalUtils"
import { postClientProtected } from "../../clients/clients"
import { LCT_RAISE_ALERT, TTR_RAISE_ALERT } from "../../urls/private"

interface IRaiseAlertHook {
    isSending: boolean
    error: IAppError | undefined
    postResponse: (
        deviceSystemRef: string,
        description: string,
        dataset?: Datasets
    ) => Promise<void>
}

/**
 * Provides functions and logic for posting a raise alert request
 * on a TTR or LCT device.
 * @returns {IRaiseAlertHook} - loading and error states.
 */
const useRaiseAlert = (
    setIsOpen: Dispatch<SetStateAction<boolean>>
): IRaiseAlertHook => {
    const [isSending, setIsSending] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)

    const postResponse = async (
        deviceSystemRef: string,
        description: string,
        dataset?: Datasets
    ): Promise<void> => {
        try {
            setIsSending(true)

            // Build response
            const body = {
                deviceSystemRef,
                description,
            }
            if (
                dataset &&
                ![Datasets.TTR, Datasets.TTR_DRAFT].includes(dataset)
            ) {
                await postClientProtected(LCT_RAISE_ALERT(dataset), body)
            } else {
                await postClientProtected(TTR_RAISE_ALERT, body)
            }
            snackbar("Alert raised successfully", "success")
            setError(undefined)
            setIsOpen(false)
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
        } finally {
            setIsSending(false)
        }
    }

    return {
        error,
        isSending,
        postResponse,
    }
}

export default useRaiseAlert
